// src/components/Inbox.tsx
import { buildSubscriber } from "@novu/js/internal";
import React, { useMemo } from "react";
import { useNovuUI } from "../context/NovuUIContext.js";
import { useRenderer } from "../context/RendererContext.js";
import { InternalNovuProvider, useNovu, useUnsafeNovu } from "../hooks/NovuProvider.js";
import { Mounter } from "./Mounter.js";
import { NovuUI } from "./NovuUI.js";
import { withRenderer } from "./Renderer.js";
import { jsx } from "react/jsx-runtime";
var DefaultInbox = (props) => {
  const {
    open,
    renderNotification,
    renderAvatar,
    renderSubject,
    renderBody,
    renderDefaultActions,
    renderCustomActions,
    renderBell,
    onNotificationClick,
    onPrimaryActionClick,
    onSecondaryActionClick,
    placement,
    placementOffset
  } = props;
  const { novuUI } = useNovuUI();
  const { mountElement } = useRenderer();
  const mount = React.useCallback(
    (element) => {
      if (renderNotification) {
        return novuUI.mountComponent({
          name: "Inbox",
          props: {
            open,
            renderNotification: renderNotification ? (el, notification) => mountElement(el, renderNotification(notification)) : void 0,
            renderBell: renderBell ? (el, unreadCount) => mountElement(el, renderBell(unreadCount)) : void 0,
            onNotificationClick,
            onPrimaryActionClick,
            onSecondaryActionClick,
            placementOffset,
            placement
          },
          element
        });
      }
      return novuUI.mountComponent({
        name: "Inbox",
        props: {
          open,
          renderAvatar: renderAvatar ? (el, notification) => mountElement(el, renderAvatar(notification)) : void 0,
          renderSubject: renderSubject ? (el, notification) => mountElement(el, renderSubject(notification)) : void 0,
          renderBody: renderBody ? (el, notification) => mountElement(el, renderBody(notification)) : void 0,
          renderDefaultActions: renderDefaultActions ? (el, notification) => mountElement(el, renderDefaultActions(notification)) : void 0,
          renderCustomActions: renderCustomActions ? (el, notification) => mountElement(el, renderCustomActions(notification)) : void 0,
          renderBell: renderBell ? (el, unreadCount) => mountElement(el, renderBell(unreadCount)) : void 0,
          onNotificationClick,
          onPrimaryActionClick,
          onSecondaryActionClick,
          placementOffset,
          placement
        },
        element
      });
    },
    [
      open,
      renderNotification,
      renderAvatar,
      renderSubject,
      renderBody,
      renderDefaultActions,
      renderCustomActions,
      renderBell,
      onNotificationClick,
      onPrimaryActionClick,
      onSecondaryActionClick
    ]
  );
  return /* @__PURE__ */ jsx(Mounter, { mount });
};
var Inbox = React.memo((props) => {
  const { subscriberId, ...propsWithoutSubscriberId } = props;
  const subscriber = useMemo(
    () => buildSubscriber({ subscriberId: props.subscriberId, subscriber: props.subscriber }),
    [props.subscriberId, props.subscriber]
  );
  const applicationIdentifier = props.applicationIdentifier ? props.applicationIdentifier : "";
  const novu = useUnsafeNovu();
  if (novu) {
    return /* @__PURE__ */ jsx(InboxChild, { ...propsWithoutSubscriberId, applicationIdentifier, subscriber });
  }
  const providerProps = {
    applicationIdentifier,
    subscriberHash: props.subscriberHash,
    contextHash: props.contextHash,
    backendUrl: props.backendUrl,
    socketUrl: props.socketUrl,
    subscriber,
    defaultSchedule: props.defaultSchedule,
    context: props.context
  };
  return /* @__PURE__ */ jsx(InternalNovuProvider, { ...providerProps, children: /* @__PURE__ */ jsx(InboxChild, { ...propsWithoutSubscriberId, applicationIdentifier, subscriber }) });
});
var InboxChild = withRenderer(
  React.memo((props) => {
    const {
      localization,
      appearance,
      tabs,
      preferencesFilter,
      preferenceGroups,
      preferencesSort,
      routerPush,
      applicationIdentifier = "",
      // for keyless we provide an empty string, the api will generate a identifier
      subscriberId,
      subscriberHash,
      contextHash,
      backendUrl,
      socketUrl,
      subscriber,
      defaultSchedule,
      context
    } = props;
    const novu = useNovu();
    const options = useMemo(() => {
      return {
        localization,
        appearance,
        tabs,
        preferencesFilter,
        preferenceGroups,
        preferencesSort,
        routerPush,
        options: {
          applicationIdentifier,
          subscriberHash,
          contextHash,
          backendUrl,
          socketUrl,
          subscriber: buildSubscriber({ subscriberId, subscriber }),
          defaultSchedule,
          context
        }
      };
    }, [
      localization,
      appearance,
      tabs,
      preferencesFilter,
      preferenceGroups,
      preferencesSort,
      applicationIdentifier,
      subscriberId,
      subscriberHash,
      contextHash,
      backendUrl,
      socketUrl,
      subscriber,
      context
    ]);
    if (isWithChildrenProps(props)) {
      return /* @__PURE__ */ jsx(NovuUI, { options, novu, children: props.children });
    }
    const {
      open,
      renderNotification,
      renderAvatar,
      renderSubject,
      renderBody,
      renderDefaultActions,
      renderCustomActions,
      renderBell,
      onNotificationClick,
      onPrimaryActionClick,
      onSecondaryActionClick,
      placementOffset,
      placement
    } = props;
    return /* @__PURE__ */ jsx(NovuUI, { options, novu, children: /* @__PURE__ */ jsx(
      DefaultInbox,
      {
        open,
        renderNotification,
        renderAvatar,
        renderSubject,
        renderBody,
        renderDefaultActions,
        renderCustomActions,
        renderBell,
        onNotificationClick,
        onPrimaryActionClick,
        onSecondaryActionClick,
        placement,
        placementOffset
      }
    ) });
  })
);
InboxChild.displayName = "InboxChild";
function isWithChildrenProps(props) {
  return "children" in props;
}
export {
  Inbox
};
//# sourceMappingURL=Inbox.js.map